import { useMemo } from "react";
import PropTypes from 'prop-types';
import Iconify from "@heyjob-dash/shared/components/iconify/index";
import CustomPopover from "@heyjob-dash/shared/components/custom-popover/index";

import { Box , Alert , Stack , Avatar , MenuList , useTheme, MenuItem, TextField, ButtonBase, Typography, Autocomplete, useMediaQuery, InputAdornment } from '@mui/material';

import { usePopover } from 'src/components/custom-popover';

import { useAuthContext } from '../../auth/hooks/index';

// ----------------------------------------------------------------------

export function WorkspacesPopover({ data = [], sx, ...other }) {
  const theme = useTheme();

  const mediaQuery = 'sm';

  const sm = useMediaQuery(theme.breakpoints.down(mediaQuery));

  const { open, onClose, onOpen } = usePopover();

  const { user, selectCompany } = useAuthContext();

  const currentCompany = useMemo(() => user?.data?.current_company != null ? user?.data?.companies[user?.data?.current_company] : null, [user?.data]);

  const handleChangeWorkspace = (option) => {
    selectCompany(option.id);
    onClose();
  };

  const getCompanyShort = (company) => {
    if (company?.name) {
      if (company.name.split(' ').length > 1) {
        return company.name.split(' ')[0].charAt(0) + company.name.split(' ')[1].charAt(0);
      }
      return company.name.slice(0, 2);

    }
    return '';
  }

  const buttonBg = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (t) =>
      t.transitions.create(['opacity', 'visibility'], {
        easing: t.transitions.easing.sharp,
        duration: t.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  const mobileSelector = (
    <ButtonBase
      disableRipple
      onClick={onOpen}
      sx={[
        {
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          '&::before': buttonBg,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Avatar alt={currentCompany?.name}
              src={currentCompany?.logo_url}
              sx={{ width: 24, height: 24 }}>
        <Typography sx={{ fontSize: 12 }}>
          {getCompanyShort(currentCompany)}
        </Typography>
      </Avatar>

      <Box
        component="span"
        sx={{
          typography: 'subtitle2',
          display: 'inline-flex',
          maxWidth: 120,
          ml: 0.5,
          pr: 0.5,
          textAlign: 'left',
          fontSize: 12,
          textOverflow: 'ellipsis'
        }}
      >
        {currentCompany?.name}
      </Box>

      { /* <Label
        color={workspace?.plan === 'Free' ? 'default' : 'info'}
        sx={{
          height: 22,
          cursor: 'inherit',
          display: { xs: 'none', [mediaQuery]: 'inline-flex' },
        }}
      >
        {workspace?.plan}
      </Label> */}

      <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }}/>
    </ButtonBase>
  )

  const desktopSelector = (
    <Autocomplete
      size="small"
      value={user?.data?.current_company || ''}
      getOptionLabel={(option) => user?.data?.companies[option]?.name}
      options={data.map((o) => o.id)}
      disableClearable
      getOptionDisabled={(option) => !['active', 'onboarding'].includes(user?.data?.companies[option]?.status)}
      filterOptions={(options, params) => options.filter((o) => [user?.data?.companies[o]?.name, user?.data?.companies[o]?.code].some((field) => field?.toLowerCase().includes(params.inputValue.toLowerCase())))}
      onChange={(event, newValue) =>
        handleChangeWorkspace(data.find((o) => o.id === newValue))
      }
      renderOption={(props, option) => {
        const company = user.data?.companies && Object.values(user.data.companies)?.find((o) => o.id === option);
        // const company = data.find((o) => o.id === option);
        return (
          <li {...props} key={company.name}>
            <Avatar alt={company.name}
                    src={company.logo_url}
                    sx={{ width: 24, height: 24, mr: 2, my: 0.5 }}/>
            <Stack direction="column">
              {company.name}
              {company.status !== 'active' && <Typography variant="caption" component="div" color="error">{company.status === 'onboarding' ? 'Bozza' : 'Sospesa'}</Typography>}
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => {
        const company = user.data?.companies && Object.values(user.data.companies)?.find((o) => o.name === params.inputProps?.value);

        const baseField = {
          ...params,
          sx: {
            minWidth: 240,
          },
          inputProps: {
            ...params.inputProps,
            autoComplete: 'new-password',
            value: company?.name
          },
        };

        return (
          <TextField
            {...baseField}
            error={!user?.data?.current_company}
            InputProps={{
              ...params.InputProps,

              startAdornment: company ? (
                <InputAdornment
                  position="start"
                >

                  <Avatar alt={company?.name}
                          src={company?.logo_url}
                          sx={{ width: 24, height: 24 }}/>
                </InputAdornment>
              ) : undefined,
            }}
          />
        );
      }}
    />
  )

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      onClose={onClose}
      slotProps={{
        arrow: { placement: 'top-left' },
      }}
    >
      <MenuList sx={{ width: 240 }}>
        {data.map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === user?.data?.current_company}
            onClick={() => handleChangeWorkspace(option)}
            sx={{ height: 48 }}
          >
            <Avatar alt={option.name} src={option.logo} sx={{ width: 24, height: 24 }}>
              <Typography sx={{ fontSize: 12 }}>
                {getCompanyShort(option)}
              </Typography>
            </Avatar>

            <Box component="span" sx={{ ml: 1, flexGrow: 1, fontWeight: 'fontWeightMedium' }}>
              {option.name}
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </CustomPopover>
  );

  return (<Stack direction="row" sx={{ ml: 1 }}>
    {sm ? mobileSelector : desktopSelector}
    {renderMenuList()}
    {!sm && !user?.data?.current_company && <Alert severity="error" sx={{ ml: 2 }}>
      Seleziona l&apos;azienda per continuare
    </Alert>}
  </Stack>);
}

WorkspacesPopover.propTypes = {
  data: PropTypes.array,
  sx: PropTypes.object
};
